
import { useEffect, useRef, forwardRef, useImperativeHandle, useContext } from 'react';
import * as THREE from 'three';
import { GUI } from 'lil-gui';
import { useThreeCore } from '../hooks/useThreeCore';
import { ConfigContext } from '../contexts/config-context';
import { createNoiseTexture, createHexagonalSphere, createDigitTexture, createDotTexture } from '../lib/three-utils';
import { AnimationLoop } from '../lib/three-animation-loop';
import { ShootingStars, GlowOrbs } from '../lib/three-particles';
import { setupGUI } from '../lib/three-gui';

const tooltips = {
  evolutionSpeed: "Sistemin genel gelişim ve parçacık oluşturma hızını ayarlar.",
  radius: "Merkezdeki ana şeklin yarıçapını, yani boyutunu değiştirir.",
  detail: "Ana şeklin poligon detay seviyesini artırır. Daha yüksek değerler daha pürüzsüz bir yüzey oluşturur.",
  wobbleStrength: "Sıvı benzeri dalgalanma efektinin gücünü belirler. 0'da efekt kapalıdır.",
  wobbleSpeed: "Sıvı dalgalanma efektinin hızını ayarlar.",
  rotSpeedX: "Şeklin X ekseni etrafındaki salınım hızını kontrol eder.",
  rotSpeedY: "Şeklin Y ekseni etrafındaki dönüş hızını kontrol eder.",
  rotSpeedZ: "Şeklin Z ekseni etrafındaki eğim hızını kontrol eder.",
  color: "Ana şeklin temel rengini değiştirir.",
  emissive: "Şeklin içten yaydığı parıltının rengini belirler.",
  emissiveIntensity: "İç parıltının yoğunluğunu ve parlaklığını ayarlar.",
  coreGlowMode: "Çekirdek parlaklık modu — Pulse: yanıp söner, Steady: sabit (config değeri), Always Bright: sürekli en parlak.",
  wobbleEnabled: "Çekirdeğin sıvı dalgalanma (wobble) animasyonunu açar/kapatır. Kapalıyken şekil sabit durur.",
  rotationEnabled: "Çekirdek ve parçacıkların dönüş/salınım animasyonunu açar/kapatır. Kapalıyken hareketsiz durur.",
  opacity: "Şeklin genel şeffaflığını kontrol eder. 1 tam opak, 0 tam şeffaf.",
  transmission: "Malzemenin ışığı ne kadar geçirdiğini belirler. Yüksek değerler cam gibi bir görünüm verir.",
  thickness: "Işığın içinden geçtiği malzemenin sanal kalınlığını ayarlar, kırılmaları etkiler.",
  roughness: "Yüzeyin pürüzlülüğünü ayarlar. Düşük değerler parlak, yüksek değerler mat bir yüzey oluşturur.",
  ior: "Işığın kırılma indeksini (Index of Refraction) belirler. Cam için ~1.5, su için ~1.33'tür.",
  lightColor: "Şeklin merkezindeki ışığın rengini değiştirir.",
  lightIntensity: "Merkezdeki ışığın parlaklığını ve gücünü ayarlar.",
  outerRadius: "Dıştaki cam kabuğun yarıçapını, yani boyutunu değiştirir.",
  outerColor: "Dış kabuğun cam rengini ayarlar.",
  outerEmissive: "Dış kabuğun kenarlarındaki hafif parıltının rengini belirler.",
  outerEmissiveIntensity: "Dış kabuğun kenar parıltısının yoğunluğunu ayarlar.",
  outerOpacity: "Dış kabuk yüzeyinin şeffaflığını kontrol eder.",
  outerShellEdgeOpacity: "Dış kabuğu çevreleyen çizgilerin (wireframe) şeffaflığını ayarlar.",
  outerThickness: "Dış kabuk malzemesinin sanal kalınlığını ayarlar.",
  outerRoughness: "Dış kabuk yüzeyinin pürüzlülüğünü ayarlar.",
  outerTransmission: "Dış kabuğun ışığı ne kadar geçirdiğini belirler.",
  outerIor: "Dış kabuğun ışık kırma indeksini ayarlar.",
  outerEnvMapIntensity: "Dış kabuğun çevreden yansıttığı ışığın yoğunluğunu kontrol eder.",
  sceneBgColor: "Sahnenin arka plan rengini ve sis rengini ayarlar.",
  fogDensity: "Sahnedeki sisin yoğunluğunu kontrol eder. Yüksek değerler daha yoğun sis oluşturur.",
  toneMappingExposure: "Genel sahne parlaklığını ve pozlamasını ayarlar.",
  bloomStrength: "Parlak alanların etrafındaki parıltı (bloom) efektinin gücünü belirler.",
  bloomRadius: "Parıltı efektinin yayılma alanını ve yarıçapını kontrol eder.",
  bloomThreshold: "Bir nesnenin parlamaya başlaması için gereken minimum parlaklık seviyesini ayarlar.",
  filmGrain: "Eski film efektini taklit eden gren (noise) miktarını ayarlar.",
  outerNetColor: "Dış ağ yapısının (bağlantı çizgilerinin) rengini ayarlar.",
  outerNetOpacity: "Dış ağ yapısının (bağlantı çizgilerinin) şeffaflığını ayarlar.",
  bgPointsColor: "Arka plandaki yıldız benzeri noktaların rengini değiştirir.",
  bgPointsSize: "Arka plandaki yıldız benzeri noktaların boyutunu ayarlar.",
  bgStarsEnabled: "Arka plan yıldızlarını gösterir/gizler.",
  bgStarCount: "Arka plandaki yıldız sayısını ayarlar.",
  shootingStarsEnabled: "Arada bir gökyüzünü geçen yıldız kaymalarını (meteor) açar/kapatır.",
  shootingStarFrequency: "Yıldız kayması sıklığını ayarlar (yüksek = daha sık).",
  glowEnabled: "Arka plandaki yumuşak (blur'lu) ışık yuvarlaklarını açar/kapatır.",
  glowCount: "Işık yuvarlağı sayısı.",
  glowColor: "Işık yuvarlaklarının rengi.",
  glowSize: "Işık yuvarlaklarının boyutu.",
  glowSoftness: "Bulanıklık (blur) miktarı — 0 keskin, 1 çok yumuşak.",
  glowOpacity: "Işık yuvarlaklarının yoğunluğu (şeffaflık).",
  glowSpeed: "Yuvarlakların süzülme/nabız animasyon hızı (0 = sabit).",
  streamColor: "Arka planda akan veri akışlarının (stream) rengini belirler.",
  particleColor: "Çekirdekten yayılan ve dış ağı oluşturan parçacıkların rengini ayarlar.",
  packetColor: "Ağ üzerinde hareket eden veri paketlerinin rengini belirler.",
  innerWireframeColor: "Merkezdeki şeklin tel kafes (wireframe) çizgilerinin rengini değiştirir.",
  innerWireframeOpacity: "Merkezdeki şeklin tel kafes (wireframe) çizgilerinin şeffaflığını ayarlar.",
  outerShellEdgeColor: "Dış cam kabuğun kenar çizgilerinin rengini ayarlar.",
  cameraFov: "Sets the camera field of view. Lower values feel zoomed-in/telephoto, higher values give a wide-angle look.",
  cameraAutoRotate: "Automatically orbits the camera around the scene.",
  cameraAutoRotateSpeed: "Auto-rotation speed and direction (negative = reverse).",
  cameraEnableZoom: "Enables zooming with the mouse wheel / pinch.",
  cameraEnablePan: "Enables panning the camera with right-click / two-finger drag.",
  cameraDamping: "Adds smooth damping (inertia) to camera movement.",
  cameraLocked: "Locks the camera — rotation and auto-rotate turn off, the view stays at the saved angle (pan/zoom still follow their toggles).",
  saveCameraView: "Saves the current camera angle, distance and target into the config (for a fixed view).",
  cameraDistance: "Camera distance from the target (zoom level).",
  cameraAzimuth: "Camera horizontal (left-right) angle in radians.",
  cameraPolar: "Camera vertical (up-down) angle in radians.",
  cameraTargetX: "Çerçeveyi yatay kaydırır (lens shift) — çekirdek uzayda sabit kalır, sadece kadraj kayar.",
  cameraTargetY: "Çerçeveyi dikey kaydırır (lens shift) — çekirdek sabit, kadraj kayar.",
  cameraTargetZ: "Derinlik (dolly) — kamerayı ileri/geri taşır; çekirdek sabit, pivot çekirdekte kalır.",
  cameraFrameDrag: "Ctrl + sol-tık sürükle ile çerçeve kaydırmayı (frame offset) açar/kapatır. Embed'de kilitlemek için kapat.",
};

// Frame offset = lens shift (X/Y) — çekirdek/sahne uzayda SABİT kalır, sadece
// kadraj kayar; orbit pivotu da çekirdekte (origin) kalır.
export function applyFrameOffset(camera, renderer, p) {
  if (!camera || !renderer) return;
  const sx = p.cameraTargetX || 0;
  const sy = p.cameraTargetY || 0;
  if (sx === 0 && sy === 0) {
    if (camera.view && camera.view.enabled) camera.clearViewOffset();
    return;
  }
  const sz = renderer.getSize(new THREE.Vector2());
  const fw = sz.x || 1, fh = sz.y || 1;
  // Birim başına ekranın ~%4'ü kadar kaydır (negatif X = element sağa)
  camera.setViewOffset(fw, fh, -sx * fw * 0.04, sy * fh * 0.04, fw, fh);
}

// Kamera pozu: çekirdek sabit (target=origin), Z = dolly (derinlik), X/Y = lens shift.
function applyCameraPose(camera, controls, renderer, p) {
  if (!camera || !controls) return;
  controls.target.set(0, 0, 0);
  const baseDist = p.cameraDistance != null ? p.cameraDistance : camera.position.distanceTo(controls.target);
  const radius = Math.max(0.5, baseDist + (p.cameraTargetZ || 0));
  const azimuth = p.cameraAzimuth != null ? p.cameraAzimuth : controls.getAzimuthalAngle();
  const polar = p.cameraPolar != null ? p.cameraPolar : controls.getPolarAngle();
  const offset = new THREE.Vector3().setFromSpherical(new THREE.Spherical(radius, polar, azimuth));
  camera.position.copy(controls.target).add(offset);
  camera.lookAt(controls.target);
  applyFrameOffset(camera, renderer, p);
  controls.update();
}

export const ThreeScene = forwardRef(({ initialParams, onParamsChange, onStatsChange, guiContainerRef, isEmbedMode = false }, ref) => {
  const mountRef = useRef(null);
  const { scene, camera, renderer, composer, filmPass, bloomPass, controls } = useThreeCore(mountRef);
  const sceneDataRef = useRef({});
  const configContext = useContext(ConfigContext);
  const isConfigOpen = configContext?.isConfigOpen;

  useImperativeHandle(ref, () => ({
    captureScreenshot: () => {
      if (renderer && composer) {
        composer.render();
        return renderer.domElement.toDataURL('image/jpeg', 0.7);
      }
      return null;
    }
  }));

  // Main setup effect, runs only once
  useEffect(() => {
    if (!scene || !camera || !controls || !renderer || !composer || !filmPass || !bloomPass) return;
    if (!isEmbedMode && !guiContainerRef?.current) return;

    const data = sceneDataRef.current;
    data.params = { ...initialParams };

    // Camera & controls initial config from params
    if (data.params.cameraFov != null) { camera.fov = data.params.cameraFov; camera.updateProjectionMatrix(); }
    controls.autoRotateSpeed = data.params.cameraAutoRotateSpeed ?? 1.0;
    controls.enableZoom = data.params.cameraEnableZoom !== false;
    controls.enablePan = !!data.params.cameraEnablePan;
    controls.enableDamping = data.params.cameraDamping !== false;
    applyCameraPose(camera, controls, renderer, data.params);
    const _locked0 = !!data.params.cameraLocked;
    controls.enableRotate = !_locked0;
    controls.autoRotate = _locked0 ? false : !!data.params.cameraAutoRotate;
    controls.update();

    // Manuel orbit/zoom bittiğinde değerleri params'a yaz (varyantla kaydedilsin)
    const syncCamParams = (obj) => { Object.assign(data.params, obj); if (onParamsChange) onParamsChange({ ...data.params }); };
    const onControlsEnd = () => {
      syncCamParams({
        cameraAzimuth: controls.getAzimuthalAngle(),
        cameraPolar: controls.getPolarAngle(),
        cameraDistance: controls.getDistance() - (data.params.cameraTargetZ || 0),
      });
    };
    controls.addEventListener('end', onControlsEnd);

    // Ctrl + sol-tık sürükle → frame offset (lens shift). Çekirdek sabit kalır.
    let _drag = false, _lx = 0, _ly = 0;
    const onPD = (e) => {
      if (e.button === 0 && e.ctrlKey && data.params.cameraFrameDrag !== false) { _drag = true; _lx = e.clientX; _ly = e.clientY; controls.enableRotate = false; e.preventDefault(); }
    };
    const onPM = (e) => {
      if (!_drag) return;
      const dx = e.clientX - _lx, dy = e.clientY - _ly; _lx = e.clientX; _ly = e.clientY;
      data.params.cameraTargetX = (data.params.cameraTargetX || 0) - dx * 0.03;
      data.params.cameraTargetY = (data.params.cameraTargetY || 0) + dy * 0.03;
      applyFrameOffset(camera, renderer, data.params);
    };
    const onPU = () => {
      if (!_drag) return; _drag = false;
      controls.enableRotate = !data.params.cameraLocked;
      syncCamParams({ cameraTargetX: data.params.cameraTargetX, cameraTargetY: data.params.cameraTargetY });
    };
    const _dom = renderer.domElement;
    _dom.addEventListener('pointerdown', onPD);
    window.addEventListener('pointermove', onPM);
    window.addEventListener('pointerup', onPU);
    const onResizeLens = () => applyFrameOffset(camera, renderer, data.params);
    window.addEventListener('resize', onResizeLens);
    data._camCleanup = () => {
      controls.removeEventListener('end', onControlsEnd);
      _dom.removeEventListener('pointerdown', onPD);
      window.removeEventListener('pointermove', onPM);
      window.removeEventListener('pointerup', onPU);
      window.removeEventListener('resize', onResizeLens);
    };

    // Update scene/renderer/passes with initial params
    scene.background = new THREE.Color(data.params.sceneBgColor);
    scene.fog.color.set(data.params.sceneBgColor);
    scene.fog.density = data.params.fogDensity;
    renderer.toneMappingExposure = data.params.toneMappingExposure;
    bloomPass.strength = data.params.bloomStrength;
    bloomPass.radius = data.params.bloomRadius;
    bloomPass.threshold = data.params.bloomThreshold;
    filmPass.uniforms.intensity.value = data.params.filmGrain;

    data.textures = {
        texZero: createDigitTexture("0", "#00ffff"),
        texOne: createDigitTexture("1", "#00ffff"),
        texDot: createDotTexture()
    };
    data.liquidNormalMap = createNoiseTexture();

    data.gridGroup = new THREE.Group(); scene.add(data.gridGroup);
    data.coreGroup = new THREE.Group(); scene.add(data.coreGroup);
    data.particleGroup = new THREE.Group(); scene.add(data.particleGroup);
    data.streamGroup = new THREE.Group(); data.streamGroup.position.z = -15; scene.add(data.streamGroup);

    // Arka plan yıldız alanı — gerçekçi parıltı (glow + twinkle), küresel kabuk, fog'suz.
    data.starUniforms = {
      uTime: { value: 0 },
      uColor: { value: new THREE.Color(data.params.bgPointsColor) },
      uSizeScale: { value: data.params.bgPointsSize }
    };
    data.bgPointsMat = new THREE.ShaderMaterial({
      uniforms: data.starUniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: `
        attribute float aSize;
        attribute float aPhase;
        uniform float uTime;
        uniform float uSizeScale;
        varying float vTw;
        void main(){
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          float tw = 0.4 + 0.6 * (0.5 + 0.5 * sin(uTime * 1.6 + aPhase));
          vTw = tw;
          float ps = aSize * uSizeScale * 3000.0 * tw / max(1.0, -mv.z);
          gl_PointSize = clamp(ps, 1.0, 14.0);
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        varying float vTw;
        void main(){
          vec2 c = gl_PointCoord - 0.5;
          float d = length(c);
          if (d > 0.5) discard;
          float halo = smoothstep(0.5, 0.0, d);
          float core = smoothstep(0.16, 0.0, d);
          vec3 col = mix(uColor, vec3(1.0), core * 0.85);
          float a = (halo * halo * 0.55 + core) * vTw;
          gl_FragColor = vec4(col, a);
        }
      `
    });
    const updateStarfield = () => {
      if (data.bgPoints) { data.gridGroup.remove(data.bgPoints); data.bgPoints.geometry.dispose(); data.bgPoints = null; }
      const n = Math.max(0, Math.floor(data.params.bgStarCount ?? 300));
      const pos = new Float32Array(n * 3);
      const sizes = new Float32Array(n);
      const phases = new Float32Array(n);
      for (let i = 0; i < n; i++) {
        const u = Math.random(), v = Math.random();
        const theta = 2 * Math.PI * u;
        const phi = Math.acos(2 * v - 1);
        const r = 45 + Math.random() * 45;
        pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
        pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
        pos[i * 3 + 2] = r * Math.cos(phi);
        const t = Math.random();
        sizes[i] = 0.35 + t * t * 2.4;            // çoğu küçük, az sayıda büyük parlak
        phases[i] = Math.random() * Math.PI * 2;  // twinkle fazı
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
      geo.setAttribute("aSize", new THREE.BufferAttribute(sizes, 1));
      geo.setAttribute("aPhase", new THREE.BufferAttribute(phases, 1));
      data.bgPoints = new THREE.Points(geo, data.bgPointsMat);
      data.bgPoints.visible = data.params.bgStarsEnabled !== false;
      data.gridGroup.add(data.bgPoints);
    };
    data.updateStarfield = updateStarfield;
    updateStarfield();

    // Yıldız kayması (meteor) sistemi — statik gökyüzü grubuna eklenir.
    data.shootingStars = new ShootingStars(data.gridGroup, data.params.bgPointsColor, camera);
    data.shootingStars.enabled = data.params.shootingStarsEnabled !== false;
    data.shootingStars.frequency = data.params.shootingStarFrequency ?? 1;

    // Arka plan yumuşak ışık yuvarlakları (glow)
    data.glowOrbs = new GlowOrbs(scene, data.params.glowColor);

    data.liquidGlassMat = new THREE.MeshPhysicalMaterial({
      color: data.params.color, emissive: data.params.emissive, emissiveIntensity: data.params.emissiveIntensity,
      metalness: data.params.metalness, roughness: data.params.roughness, transmission: data.params.transmission,
      thickness: data.params.thickness, ior: data.params.ior, clearcoat: 1.0, clearcoatRoughness: 0.1,
      normalMap: data.liquidNormalMap, normalScale: new THREE.Vector2(0.5, 0.5),
      side: THREE.DoubleSide, transparent: true, opacity: data.params.opacity
    });

    data.outerGlassMat = new THREE.MeshPhysicalMaterial({
      color: data.params.outerColor, emissive: data.params.outerEmissive, emissiveIntensity: data.params.outerEmissiveIntensity,
      metalness: 0.0, roughness: data.params.outerRoughness, transmission: data.params.outerTransmission,
      thickness: data.params.outerThickness, ior: data.params.outerIor, transparent: true, opacity: data.params.outerOpacity,
      side: THREE.FrontSide, clearcoat: 1.0, clearcoatRoughness: 0.02, reflectivity: 1.0,
      envMapIntensity: data.params.outerEnvMapIntensity, depthWrite: false, depthTest: true
    });

    data.glassEdgesMat = new THREE.LineBasicMaterial({
      color: data.params.outerShellEdgeColor, transparent: true, opacity: data.params.outerShellEdgeOpacity, linewidth: 1
    });

    data.innerLight = new THREE.PointLight(data.params.lightColor, data.params.lightIntensity, 15);
    data.coreGroup.add(data.innerLight);

    const rimLight1 = new THREE.PointLight(0xffffff, 2, 20); rimLight1.position.set(5, 5, 5); scene.add(rimLight1);
    const rimLight2 = new THREE.PointLight(0x0088ff, 2, 20); rimLight2.position.set(-5, -5, 5); scene.add(rimLight2);
    const glassHighlight1 = new THREE.PointLight(0xffffff, 3, 15); glassHighlight1.position.set(-5, 8, 8); scene.add(glassHighlight1);
    const glassHighlight2 = new THREE.PointLight(0xccffff, 2, 15); glassHighlight2.position.set(6, 5, 6); scene.add(glassHighlight2);

    const updateCoreGeometry = () => {
      if (data.prismMesh) { data.coreGroup.remove(data.prismMesh); data.prismMesh.geometry.dispose(); }
      if (data.wireframeMesh) { data.coreGroup.remove(data.wireframeMesh); data.wireframeMesh.geometry.dispose(); }

      const geometry = createHexagonalSphere(data.params.radius, data.params.detail);
      
      data.originalPositions = [];
      const posAttribute = geometry.attributes.position;
      for (let i = 0; i < posAttribute.count; i++) {
          data.originalPositions.push({ x: posAttribute.getX(i), y: posAttribute.getY(i), z: posAttribute.getZ(i) });
      }

      data.liquidGlassMat.flatShading = true;
      data.liquidGlassMat.needsUpdate = true;

      data.prismMesh = new THREE.Mesh(geometry, data.liquidGlassMat);
      data.coreGroup.add(data.prismMesh);

      data.innerWireframeMat = new THREE.LineBasicMaterial({ color: data.params.innerWireframeColor, transparent: true, opacity: data.params.innerWireframeOpacity, linewidth: 2 });
      const thresholdAngle = data.params.detail === 0 ? 15 : 25 + (data.params.detail * 5);
      const edges = new THREE.EdgesGeometry(geometry, thresholdAngle);
      data.wireframeMesh = new THREE.LineSegments(edges, data.innerWireframeMat);
      data.wireframeMesh.scale.setScalar(1.005);
      data.coreGroup.add(data.wireframeMesh);

      if (data.animationLoop) {
        data.animationLoop.prismMesh = data.prismMesh;
        data.animationLoop.originalPositions = data.originalPositions;
      }
    };
    data.updateCoreGeometry = updateCoreGeometry;

    const updateOuterShellGeometry = () => {
      if (data.outerGlassMesh) { data.particleGroup.remove(data.outerGlassMesh); data.outerGlassMesh.geometry.dispose(); }
      if (data.glassEdgesMesh) { data.particleGroup.remove(data.glassEdgesMesh); data.glassEdgesMesh.geometry.dispose(); }

      const outerGlassGeo = new THREE.IcosahedronGeometry(data.params.outerRadius, 5);
      data.outerGlassMesh = new THREE.Mesh(outerGlassGeo, data.outerGlassMat);
      data.outerGlassMesh.renderOrder = 999;
      data.particleGroup.add(data.outerGlassMesh);

      const glassEdgesGeo = new THREE.EdgesGeometry(outerGlassGeo, 1);
      data.glassEdgesMesh = new THREE.LineSegments(glassEdgesGeo, data.glassEdgesMat);
      data.glassEdgesMesh.renderOrder = 1000;
      data.particleGroup.add(data.glassEdgesMesh);
    };
    data.updateOuterShellGeometry = updateOuterShellGeometry;

    updateCoreGeometry();
    updateOuterShellGeometry();

    if (!isEmbedMode && guiContainerRef?.current) {
      data.gui = new GUI({ container: guiContainerRef.current });
      const updateParams = (key, value) => {
        data.params[key] = value;
        if (onParamsChange) onParamsChange({ ...data.params });
      };
      setupGUI(data.gui, data.params, {
          updateParams, updateCoreGeometry, updateOuterShellGeometry,
          liquidGlassMat: data.liquidGlassMat, outerGlassMat: data.outerGlassMat, glassEdgesMat: data.glassEdgesMat,
          innerLight: data.innerLight,
          scene, renderer, bloomPass, filmPass,
          bgPointsMat: data.bgPointsMat,
          innerWireframeMat: data.innerWireframeMat,
          camera, controls,
          updateStarfield: data.updateStarfield,
      }, tooltips);
    }

    data.animationLoop = new AnimationLoop({
        composer, filmPass, params: data.params, onStatsChange, controls, starMat: data.bgPointsMat, shootingStars: data.shootingStars, glowOrbs: data.glowOrbs,
        coreGroup: data.coreGroup, particleGroup: data.particleGroup, streamGroup: data.streamGroup,
        liquidGlassMat: data.liquidGlassMat, innerLight: data.innerLight, prismMesh: data.prismMesh, originalPositions: data.originalPositions,
        textures: data.textures, liquidNormalMap: data.liquidNormalMap
    });
    data.animationLoop.start();
    data.isInitialized = true;

    return () => {
      if (data._camCleanup) data._camCleanup();
      if (data.animationLoop) data.animationLoop.stop();
      if (data.gui) data.gui.destroy();
      while(scene.children.length > 0){ 
        const child = scene.children[0];
        scene.remove(child); 
        if (child instanceof THREE.Group) {
            while(child.children.length > 0) {
                child.remove(child.children[0]);
            }
        }
      }
      sceneDataRef.current = {};
    };
  }, [scene, camera, controls, renderer, composer, filmPass, bloomPass, guiContainerRef, onParamsChange, onStatsChange]);

  // Trigger resize when sidebar opens/closes
  useEffect(() => {
    // Trigger immediately to start the resize
    window.dispatchEvent(new Event('resize'));

    // Trigger multiple times to ensure it catches the transition
    const timeouts = [
      setTimeout(() => window.dispatchEvent(new Event('resize')), 50),
      setTimeout(() => window.dispatchEvent(new Event('resize')), 150),
      setTimeout(() => window.dispatchEvent(new Event('resize')), 250),
      setTimeout(() => window.dispatchEvent(new Event('resize')), 350),
    ];

    return () => timeouts.forEach(clearTimeout);
  }, [isConfigOpen]);

  // Also watch for sidebar width changes via ResizeObserver
  useEffect(() => {
    if (!mountRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      window.dispatchEvent(new Event('resize'));
    });

    resizeObserver.observe(mountRef.current);

    return () => resizeObserver.disconnect();
  }, [mountRef]);

  // Effect to sync with parent's `initialParams`
  useEffect(() => {
    const data = sceneDataRef.current;
    if (!data.isInitialized || !renderer || !scene || !bloomPass || !filmPass) return;

    // Prevent update loop if params are the same
    if (JSON.stringify(initialParams) === JSON.stringify(data.params)) return;

    const oldRadius = data.params.radius;
    const oldDetail = data.params.detail;
    const oldOuterRadius = data.params.outerRadius;
    const oldStarCount = data.params.bgStarCount;
    const oldStarsEnabled = data.params.bgStarsEnabled;
    const oldPose = JSON.stringify([data.params.cameraAzimuth, data.params.cameraPolar, data.params.cameraDistance, data.params.cameraTargetX, data.params.cameraTargetY, data.params.cameraTargetZ]);

    Object.assign(data.params, initialParams);

    // Update scene, renderer, and post-processing
    scene.background.set(data.params.sceneBgColor);
    scene.fog.color.set(data.params.sceneBgColor);
    scene.fog.density = data.params.fogDensity;
    renderer.toneMappingExposure = data.params.toneMappingExposure;
    bloomPass.strength = data.params.bloomStrength;
    bloomPass.radius = data.params.bloomRadius;
    bloomPass.threshold = data.params.bloomThreshold;
    filmPass.uniforms.intensity.value = data.params.filmGrain;

    // Update camera & controls
    if (camera && data.params.cameraFov != null) { camera.fov = data.params.cameraFov; camera.updateProjectionMatrix(); }
    if (controls) {
      controls.autoRotateSpeed = data.params.cameraAutoRotateSpeed ?? 1.0;
      controls.enableZoom = data.params.cameraEnableZoom !== false;
      controls.enablePan = !!data.params.cameraEnablePan;
      controls.enableDamping = data.params.cameraDamping !== false;
      // Sadece poz parametreleri değiştiyse kamerayı yeniden konumla (manuel orbit'i bozmamak için)
      const newPose = JSON.stringify([data.params.cameraAzimuth, data.params.cameraPolar, data.params.cameraDistance, data.params.cameraTargetX, data.params.cameraTargetY, data.params.cameraTargetZ]);
      if (newPose !== oldPose) applyCameraPose(camera, controls, renderer, data.params);
      const _locked = !!data.params.cameraLocked;
      controls.enableRotate = !_locked;
      controls.autoRotate = _locked ? false : !!data.params.cameraAutoRotate;
    }

    // Update materials, lights, etc.
    data.liquidGlassMat.color.set(data.params.color);
    data.liquidGlassMat.emissive.set(data.params.emissive);
    data.liquidGlassMat.emissiveIntensity = data.params.emissiveIntensity;
    data.liquidGlassMat.opacity = data.params.opacity;
    data.liquidGlassMat.transmission = data.params.transmission;
    data.liquidGlassMat.thickness = data.params.thickness;
    data.liquidGlassMat.roughness = data.params.roughness;
    data.liquidGlassMat.ior = data.params.ior;

    data.innerLight.color.set(data.params.lightColor);
    data.innerLight.intensity = data.params.lightIntensity;

    data.outerGlassMat.color.set(data.params.outerColor);
    data.outerGlassMat.emissive.set(data.params.outerEmissive);
    data.outerGlassMat.emissiveIntensity = data.params.outerEmissiveIntensity;
    data.outerGlassMat.opacity = data.params.outerOpacity;
    data.glassEdgesMat.opacity = data.params.outerShellEdgeOpacity;
    data.glassEdgesMat.color.set(data.params.outerShellEdgeColor);
    data.outerGlassMat.thickness = data.params.outerThickness;
    data.outerGlassMat.roughness = data.params.outerRoughness;
    data.outerGlassMat.transmission = data.params.outerTransmission;
    data.outerGlassMat.ior = data.params.outerIor;
    data.outerGlassMat.envMapIntensity = data.params.outerEnvMapIntensity;

    if (data.innerWireframeMat) {
      data.innerWireframeMat.color.set(data.params.innerWireframeColor);
      data.innerWireframeMat.opacity = data.params.innerWireframeOpacity;
    }
    if (data.bgPointsMat && data.bgPointsMat.uniforms) {
      data.bgPointsMat.uniforms.uColor.value.set(data.params.bgPointsColor);
      data.bgPointsMat.uniforms.uSizeScale.value = data.params.bgPointsSize;
    }
    // Yıldız alanı — adet değişince yeniden kur, sadece görünürlük değişince toggle et
    if (data.params.bgStarCount !== oldStarCount) {
      if (data.updateStarfield) data.updateStarfield();
    } else if (data.params.bgStarsEnabled !== oldStarsEnabled && data.bgPoints) {
      data.bgPoints.visible = data.params.bgStarsEnabled !== false;
    }

    // Update GUI controllers
    if (data.gui && data.gui.controllers) {
      for (const controller of data.gui.controllers) {
        if (data.params.hasOwnProperty(controller.property)) {
          controller.setValue(data.params[controller.property]);
        }
      }
    }

    // Rebuild geometry if needed
    if (data.params.radius !== oldRadius || data.params.detail !== oldDetail) {
      data.updateCoreGeometry();
    }
    if (data.params.outerRadius !== oldOuterRadius) {
      data.updateOuterShellGeometry();
    }

  }, [initialParams, scene, camera, controls, renderer, bloomPass, filmPass]);

  return <div ref={mountRef} style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, zIndex: 0 }} />;
});

ThreeScene.displayName = 'ThreeScene';
